"use strict"

import React, { useCallback, useEffect, useRef, useState } from "react"
import { useIntl } from "react-intl"

import ActionForm from "./ActionForm"
import { withOperatorActionConfig } from "../../config/OperatorActionConfigProvider"
import { withAppSettingsConfig } from "../../config/AppSettingsConfigProvider"
import DashLoader from "../../shared_components/widgets/DashLoader"

import { arrayWithContents } from "../../shared_components/utils"
import RunCommentTable from "../../shared_components/graph_dashboards/LTPMDash/RunComments/RunCommentTable"
import ReactSelectionElement from "../../shared_components/widgets/ReactSelectionElement"

const OperatorAction = ({
  toggleVisibility,
  open,
  height,
  // From OperatorActionConfigProvider
  loading,
  reactors,
  actors,
  actions,
  newActionErr,
  createUpdateOrDeleteAction,
  // --- From OperatorActionConfigProvider
  defaultReactor, // From AppSettingsConfigProvider
}) => {
  const intl = useIntl()
  const resizeRef = useRef()

  const baseChevClass = "operator-action-chevrons fa"
  const rightChevClass = baseChevClass + " fa-chevron-right"
  const leftChevClass = baseChevClass + " fa-chevron-left"

  const [left, setDirection] = useState(true)
  const [reactor, setReactor] = useState("")
  const [reactorActions, setReactorActions] = useState([])
  const [selectedAssociateReactors, setSelectedAssociateReactors] = useState([])

  const [actionTabHeight, setActionTabHeight] = useState(height)
  useEffect(() => {
    setActionTabHeight(height)
  }, [height, open])

  if (resizeRef.current) {
    resizeRef.current["style"].height = `${actionTabHeight}px`
  }

  const [commentTableHeight, setCommentTableHeight] = useState(actionTabHeight)
  useEffect(() => {
    const commentTableHeight = actionTabHeight - 25 - 500 - 15 - 25 - 10
    setCommentTableHeight(commentTableHeight > 350 ? commentTableHeight : 350)
  }, [actionTabHeight])

  useEffect(() => {
    setReactor(reactor || defaultReactor || reactors[0])
    setReactorActions(actions[reactor || defaultReactor || reactors[0]])
  }, [actions, reactors])

  const [sortedActions, setSortedActions] = useState([])
  useEffect(() => {
    setSortedActions(
      reactorActions ? reactorActions.sort((a, b) => b.timestamp.valueOf() - a.timestamp.valueOf()) : [],
    )
  }, [reactorActions, reactor])

  const updateReactorParams = useCallback(
    (curr, alt) => {
      if (alt !== "" && alt !== curr && Object.keys(actions).includes(alt)) {
        setReactor(alt)
        setReactorActions(actions[alt])
      }
    },
    [actions],
  )

  useEffect(() => {
    defaultReactor && updateReactorParams(reactor, defaultReactor)
  }, [defaultReactor])

  useEffect(() => {
    // If the current reactor is in selectedAssociateReactors, remove it
    if (selectedAssociateReactors.some((r) => r === reactor)) {
      setSelectedAssociateReactors((prev) => prev.filter((r) => r !== reactor))
    }
  }, [reactor])

  const toggleChevrons = () => {
    setDirection(!left)
    updateReactorParams(reactor, defaultReactor)
    setSelectedAssociateReactors([])
    toggleVisibility()
  }

  const handleReactorSelectionChange = useCallback((_, selectedOptions) => {
    setSelectedAssociateReactors(selectedOptions.map((s) => s.value))
  }, [])

  return (
    <div id={"operator-access-component"}>
      <div
        id={"operator-action-access-tab"}
        onClick={() => toggleChevrons()}
        title={intl.formatMessage({ id: "operatoraction.journal.toggle.title" })}
      >
        <span className={left ? leftChevClass : rightChevClass} />
        <span className={left ? leftChevClass : rightChevClass} />
      </div>
      {open && (
        <div id={"operator-action-input-body"} ref={resizeRef}>
          <div ref={resizeRef}>
            <div className={"content-block"}>
              <div style={{ display: "flex" }}>
                <h4 className={"text-center"} style={{ margin: "0" }}>
                  <span className={`fa fa-pencil`} style={{ marginRight: "3px" }} />
                  {intl.formatMessage({ id: "operatoraction.journal.header" })}
                </h4>
                <button
                  style={{ marginLeft: "auto" }}
                  className={"btn-close"}
                  aria-label={"Close"}
                  onClick={() => toggleChevrons()}
                ></button>
              </div>
              <hr />
              {!loading ? (
                <>
                  <div className={"row"}>
                    {/*Reactor Dropdown*/}
                    <div className={"col-6"}>
                      <ReactSelectionElement
                        name={"primaryReactor"}
                        list={Object.keys(actions).map((s) => ({ value: s }))}
                        label={intl.formatMessage({
                          id: "operatoraction.choose.reactor",
                          defaultMessage: "Select Reactor",
                        })}
                        onValueChange={(_, val) => updateReactorParams(reactor, val?.value)}
                        value={{ value: reactor }}
                        isClearable={false}
                        isMulti={false}
                        required={true}
                      />
                    </div>
                    <div className={"col-6"}>
                      <ReactSelectionElement
                        name={"multipleReactors"}
                        list={reactors
                          .filter((r) => r !== reactor)
                          .map((s) => {
                            return { value: s }
                          })}
                        label={intl.formatMessage({
                          id: "operatoraction.associatedReactors",
                          defaultMessage: "Associated Reactors",
                        })}
                        onValueChange={handleReactorSelectionChange}
                        defaultValue={selectedAssociateReactors.map((s) => ({ value: s }))}
                        value={selectedAssociateReactors.map((s) => ({ value: s }))}
                        required={false}
                      />
                    </div>
                  </div>
                  <ActionForm
                    actors={actors}
                    reactor={reactor}
                    createUpdateOrDeleteAction={createUpdateOrDeleteAction}
                    actionErr={newActionErr}
                    intl={intl}
                    selectedAssociateReactors={selectedAssociateReactors}
                    onComplete={() => setSelectedAssociateReactors([])}
                    parentReset={() => setSelectedAssociateReactors([])}
                  />
                </>
              ) : (
                <div
                  className={"dashed-black-border"}
                  style={{
                    height: `${actionTabHeight - 100}px`,
                    position: "relative",
                  }}
                >
                  <DashLoader />
                </div>
              )}
            </div>
            <div className={"content-block"}>
              <h6>
                {intl.formatMessage(
                  { id: "operatoraction.recentComments.header" },
                  {
                    reactor: reactor,
                    count: arrayWithContents(sortedActions) ? sortedActions.length : "",
                  },
                )}
              </h6>
              <div style={{ height: `${commentTableHeight}px`, overflow: "auto" }}>
                <RunCommentTable
                  currentReactor={reactor}
                  loading={loading}
                  showTable
                  runActions={sortedActions}
                  hadActions={actions.length > 0}
                  intl={intl}
                  isOperatorAction
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default withAppSettingsConfig(withOperatorActionConfig(OperatorAction))
